import { useMemo, useState } from "react";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { UserPlus, Search, Plus } from "lucide-react";
import { usePeople } from "@/hooks/usePeople";
import { AvailabilityStatus } from "@/hooks/useProjectPeople";
import {
  AvailabilityStatusBadge,
  availabilityStatuses,
} from "./AvailabilityStatusBadge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { InlinePersonForm } from "@/components/forms/InlinePersonForm";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

interface AddPersonDialogProps {
  existingPersonIds: string[];
  planningDays: Date[];
  eventDays: Date[];
  onAdd: (
    personIds: string[],
    status: AvailabilityStatus,
    dateType: "planning" | "event" | "custom",
    customDates?: string[],
  ) => void;
  isLoading?: boolean;
}

export function AddPersonDialog({
  existingPersonIds,
  planningDays,
  eventDays,
  onAdd,
  isLoading,
}: AddPersonDialogProps) {
  const [open, setOpen] = useState(false);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [status, setStatus] = useState<AvailabilityStatus>("requested");
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [personTypeFilter, setPersonTypeFilter] = useState<
    "employee" | "freelancer" | "all"
  >("employee");
  const [dateType, setDateType] = useState<"planning" | "event" | "custom">(
    "planning",
  );
  const [customSelectedDays, setCustomSelectedDays] = useState<string[]>([]);

  // Fetch employees and freelancers only
  const { data: employees } = usePeople("employee");
  const { data: freelancers } = usePeople("freelancer");

  const availablePeople = [...(employees || []), ...(freelancers || [])]
    .filter((p) => !existingPersonIds.includes(p.id))
    .filter(
      (p) => personTypeFilter === "all" || p.person_type === personTypeFilter,
    )
    .sort((a, b) =>
      `${a.last_name} ${a.first_name}`.localeCompare(
        `${b.last_name} ${b.first_name}`,
      ),
    );

  // Filter by search query
  const filteredPeople = availablePeople.filter((person) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const fullName = `${person.first_name} ${person.last_name}`.toLowerCase();
    const reverseName =
      `${person.last_name} ${person.first_name}`.toLowerCase();
    const companyName = person.companies?.name?.toLowerCase() || "";
    return (
      fullName.includes(query) ||
      reverseName.includes(query) ||
      companyName.includes(query)
    );
  });

  const allFilteredSelected =
    filteredPeople.length > 0 &&
    filteredPeople.every((person) => selectedPersonIds.includes(person.id));

  const planningDayOptions = useMemo(
    () =>
      planningDays.map((day) => ({
        value: format(day, "yyyy-MM-dd"),
        label: format(day, "EEEE, dd.MM.yyyy"),
        shortLabel: format(day, "EEE dd.MM"),
      })),
    [planningDays],
  );
  const eventDayValues = useMemo(
    () => eventDays.map((day) => format(day, "yyyy-MM-dd")),
    [eventDays],
  );

  const handleSubmit = () => {
    if (
      selectedPersonIds.length > 0 &&
      (dateType !== "custom" || customSelectedDays.length > 0)
    ) {
      onAdd(
        selectedPersonIds,
        status,
        dateType,
        dateType === "custom" ? customSelectedDays : undefined,
      );
      setOpen(false);
      resetState();
    }
  };

  const resetState = () => {
    setSelectedPersonIds([]);
    setStatus("requested");
    setSearchQuery("");
    setShowCreateForm(false);
    setPersonTypeFilter("employee");
    setDateType("planning");
    setCustomSelectedDays([]);
  };

  const togglePerson = (personId: string) => {
    setSelectedPersonIds((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId],
    );
  };

  const handlePersonCreated = (newPersonId: string) => {
    setSelectedPersonIds((prev) => [...prev, newPersonId]);
    setShowCreateForm(false);
    setSearchQuery("");
  };

  const toggleAllFilteredPeople = () => {
    const filteredIds = filteredPeople.map((person) => person.id);
    setSelectedPersonIds((prev) =>
      allFilteredSelected
        ? prev.filter((id) => !filteredIds.includes(id))
        : [...new Set([...prev, ...filteredIds])],
    );
  };

  const toggleCustomDay = (day: string) => {
    setCustomSelectedDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort(),
    );
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      resetState();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm">
          <UserPlus className="h-4 w-4 mr-2" />
          Add from project people
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add from project people</DialogTitle>
        </DialogHeader>

        {showCreateForm ? (
          <div className="space-y-4">
            <InlinePersonForm
              onSuccess={handlePersonCreated}
              onCancel={() => setShowCreateForm(false)}
              initialName={searchQuery}
              defaultType="freelancer"
              showTypeSelector={true}
            />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <Label>Initial Status</Label>
                <Select
                  value={status}
                  onValueChange={(v) => setStatus(v as AvailabilityStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {availabilityStatuses.map((s) => (
                      <SelectItem key={s} value={s}>
                        <AvailabilityStatusBadge status={s} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Initial Days</Label>
                <ToggleGroup
                  type="single"
                  value={dateType}
                  onValueChange={(value) => {
                    if (
                      value === "planning" ||
                      value === "event" ||
                      value === "custom"
                    ) {
                      setDateType(value);
                    }
                  }}
                >
                  <ToggleGroupItem value="planning" className="text-xs">
                    Planning
                  </ToggleGroupItem>
                  <ToggleGroupItem value="event" className="text-xs">
                    Event
                  </ToggleGroupItem>
                  <ToggleGroupItem value="custom" className="text-xs">
                    Custom
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>
            </div>

            {dateType === "custom" && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <Label>Custom Days</Label>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setCustomSelectedDays(
                          planningDayOptions.map((day) => day.value),
                        )
                      }
                    >
                      All planning days
                    </Button>
                    {eventDayValues.length > 0 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCustomSelectedDays(eventDayValues)}
                      >
                        Event dates
                      </Button>
                    )}
                  </div>
                </div>

                <div className="rounded-md border">
                  <ScrollArea className="h-40 p-3">
                    <div className="space-y-2">
                      {planningDayOptions.map((day) => (
                        <label
                          key={day.value}
                          className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-muted/50"
                        >
                          <Checkbox
                            checked={customSelectedDays.includes(day.value)}
                            onCheckedChange={() => toggleCustomDay(day.value)}
                          />
                          <span className="text-sm">{day.label}</span>
                        </label>
                      ))}
                    </div>
                  </ScrollArea>
                </div>

                {customSelectedDays.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Selected:{" "}
                    {planningDayOptions
                      .filter((day) => customSelectedDays.includes(day.value))
                      .map((day) => day.shortLabel)
                      .join(", ")}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-2">
              <Label>Select People ({selectedPersonIds.length} selected)</Label>

              {/* Person type filter */}
              <ToggleGroup
                type="single"
                value={personTypeFilter}
                onValueChange={(value) => {
                  if (
                    value === "employee" ||
                    value === "freelancer" ||
                    value === "all"
                  ) {
                    setPersonTypeFilter(value);
                  }
                }}
                className="justify-start"
              >
                <ToggleGroupItem value="employee" className="text-xs">
                  Employees
                </ToggleGroupItem>
                <ToggleGroupItem value="freelancer" className="text-xs">
                  Freelancers
                </ToggleGroupItem>
                <ToggleGroupItem value="all" className="text-xs">
                  All
                </ToggleGroupItem>
              </ToggleGroup>

              {/* Search Input */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or company..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              <ScrollArea className="h-64 border rounded-md">
                {/* Create New Person Option */}
                {searchQuery.trim() && (
                  <div
                    className="flex items-center gap-3 p-3 border-b border-border hover:bg-muted cursor-pointer"
                    onClick={() => setShowCreateForm(true)}
                  >
                    <div className="flex items-center justify-center h-6 w-6 rounded-full bg-primary/10 text-primary">
                      <Plus className="h-4 w-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary">
                        Add Intercom user only
                        {searchQuery && `: "${searchQuery}"`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Create an Intercom-only employee or freelancer
                      </p>
                    </div>
                  </div>
                )}

                {filteredPeople.length === 0 ? (
                  <p className="text-sm text-muted-foreground p-4">
                    {searchQuery
                      ? "No matching people found"
                      : "No available people to add"}
                  </p>
                ) : (
                  <div className="p-2 space-y-1">
                    <div
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                      onClick={toggleAllFilteredPeople}
                    >
                      <Checkbox
                        checked={allFilteredSelected}
                        onClick={(event) => event.stopPropagation()}
                        onCheckedChange={toggleAllFilteredPeople}
                      />
                      <p className="text-sm font-medium">
                        {allFilteredSelected ? "Deselect all" : "Select all"}
                      </p>
                    </div>
                    {filteredPeople.map((person) => (
                      <div
                        key={person.id}
                        className="flex items-center gap-3 p-2 rounded-md hover:bg-muted cursor-pointer"
                        onClick={() => togglePerson(person.id)}
                      >
                        <Checkbox
                          checked={selectedPersonIds.includes(person.id)}
                          onClick={(event) => event.stopPropagation()}
                          onCheckedChange={() => togglePerson(person.id)}
                        />
                        <p className="text-sm font-medium truncate">
                          {person.first_name} {person.last_name}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  selectedPersonIds.length === 0 ||
                  isLoading ||
                  (dateType === "custom" && customSelectedDays.length === 0)
                }
              >
                Add{" "}
                {selectedPersonIds.length > 0
                  ? `(${selectedPersonIds.length})`
                  : ""}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
